import React, { memo, useCallback } from 'react'
import OfferPrice from 'components/Common/OfferPricing/Price/OfferPrice'
import { PricesContainer } from 'components/Common/OfferPricing/styles'
import useReduxStateListener from 'hooks/useReduxStateListener'
import { isLoggedIn } from 'selectors/accountSelectors'
import { pushWithRegion } from 'actions/NavigationActions'
import { getBookingPath } from 'lib/paths'
import { STEP_CAPACITY } from 'constants/booking'
import { RESERVATION_TYPE_INSTANT_BOOKING } from 'constants/reservation'
import { OFFER_TYPE_HOTEL, OFFER_TYPE_TOUR, OFFER_TYPE_LAST_MINUTE } from 'constants/offer'
import TextButton from 'components/Luxkit/Button/TextButton'

import { useAppDispatch } from 'hooks/reduxHooks'

import Spacing from 'components/Common/Spacing'

import {
  PriceContainer,
} from 'components/Common/OfferPricing/styles'
import { rem } from 'polished'
import styled from 'styled-components'
import FlashTourBuyButtons from './FlashTourBuyButtons'
import FlashTourSoldOutOrUnavailable from './FlashTourSoldOutOrUnavailable'

const StyledPriceContainer = styled(PriceContainer)`
  display: flex;
  flex-direction: column;
  padding-bottom: ${rem(6)};
  flex: 1;
`

const StyledSpacing = styled(Spacing)`
  margin-top: auto;
`

const StyledBuyButtons = styled(FlashTourBuyButtons)`
  text-align: left;

  > * + * {
    margin-top: ${rem(8)};
  }
`

interface Props {
  memberId?: string;
  availableForPurchase: boolean;
  canInstantBook?: boolean;
  durationLabel: string;
  originAirport?: App.Airport;
  offerFlightsEnabled?: boolean;
  offer: App.Offer;
  packageOption: App.Package;
  flightPrice?: number;
  shouldDisplayValue?: boolean;
  showFlights?: boolean;
  className?: string;
  accountAccessShowLogin?: () => void
}

function FlashTourPackagePricing({
  memberId,
  accountAccessShowLogin,
  durationLabel,
  packageOption,
  offer,
  availableForPurchase,
  canInstantBook,
  originAirport,
  shouldDisplayValue,
  showFlights,
  offerFlightsEnabled,
  className,
  flightPrice,
}: Props) {
  const { parentType, type, typeLabel, flightDestinationPort, saleUnit, id: offerId } = offer
  const { id: packageId, duration, discountPercent, roomType } = packageOption
  const { code: originAirportCode } = originAirport || {}

  const hasPrice = !!packageOption.price
  const flightCacheOff = showFlights && (offer.flightCacheDisabled || !offer.flightPrices[originAirport?.code])
  const originIsDestination = originAirport && flightDestinationPort === originAirportCode
  const shouldShowDiscountPercent = type === OFFER_TYPE_LAST_MINUTE
  const bookingPath = getBookingPath(STEP_CAPACITY, {
    reservationType: RESERVATION_TYPE_INSTANT_BOOKING,
    packageId,
    offerId,
  })
  const bookingWithFlightPath = getBookingPath(STEP_CAPACITY, {
    reservationType: RESERVATION_TYPE_INSTANT_BOOKING,
    packageId,
    offerId,
    originAirportCode,
    numberOfNights: duration,
    withFlights: 'true',
  })

  const loginListener = useReduxStateListener<{ loggedIn: boolean }>((state) => {
    if (isLoggedIn(state)) {
      return { loggedIn: true }
    } else if (!state.ui.modal.open) {
      return { loggedIn: false }
    }
  })

  const dispatch = useAppDispatch()

  const showRegisterModal = useCallback(() => {
    accountAccessShowLogin()

    loginListener.awaitResult().then(({ loggedIn }) => {
      if (loggedIn) {
        dispatch(pushWithRegion(bookingPath))
      }
    })
  }, [accountAccessShowLogin, bookingPath, dispatch, loginListener])

  const showRegisterModalForFlight = useCallback(() => {
    accountAccessShowLogin()

    loginListener.awaitResult().then(({ loggedIn }) => {
      if (loggedIn) {
        dispatch(pushWithRegion(bookingWithFlightPath))
      }
    })
  }, [accountAccessShowLogin, bookingWithFlightPath, dispatch, loginListener])

  return <PricesContainer className={className}>
    <StyledPriceContainer>
      <OfferPrice
        offer={offer}
        durationLabel={durationLabel}
        offerSaleUnit={saleUnit}
        label={typeLabel}
        shouldShowValue={shouldDisplayValue && !shouldShowDiscountPercent}
        showFlights={showFlights}
        price={packageOption.price}
        taxesAndFees={packageOption.taxesAndFees}
        value={packageOption.value}
        discountPercent={discountPercent}
        roomType={roomType}
        type={type}
      />
      <FlashTourSoldOutOrUnavailable
        availableForPurchase={availableForPurchase}
        hasPrice={hasPrice}
      >
        <StyledSpacing pt={12}>
          {parentType === OFFER_TYPE_TOUR && (
            <TextButton
              kind="primary"
              fit="flex"
              data-testid="TourBookingBtn"
              {...(!memberId && accountAccessShowLogin ? { onClick: showRegisterModal } : { to: bookingPath })}
            >
              Check Availability
            </TextButton>
          )}
          {parentType === OFFER_TYPE_HOTEL && canInstantBook &&
            <StyledBuyButtons
              memberId={memberId}
              accountAccessShowLogin={accountAccessShowLogin}
              flightOrigin={originAirport ? originAirportCode : undefined}
              offerId={offerId}
              packageId={packageId}
              numberOfNights={duration}
              showFlights={showFlights}
            />
          }
        </StyledSpacing>
      </FlashTourSoldOutOrUnavailable>
    </StyledPriceContainer>
    {originAirport && showFlights && offerFlightsEnabled &&
      <StyledPriceContainer>
        <OfferPrice
          offer={offer}
          hasClause
          isFlightPrice
          flightCacheOff={flightCacheOff}
          durationLabel={durationLabel}
          offerSaleUnit="package"
          label={`${typeLabel} + Flights`}
          shouldShowValue={shouldDisplayValue && !shouldShowDiscountPercent && !flightCacheOff}
          showFlights={showFlights}
          price={flightPrice && packageOption.price + flightPrice}
          taxesAndFees={flightPrice && packageOption.taxesAndFees}
          value={flightPrice && packageOption.value + flightPrice}
          data-testid="pricing-with-flights"
          originAirport={originAirport}
          noFlightsFromOrigin={originIsDestination}
          discountPercent={discountPercent}
          roomType={roomType}
          type={type}
        />
        <FlashTourSoldOutOrUnavailable
          availableForPurchase={availableForPurchase}
          hasPrice={hasPrice}>
          {!originIsDestination &&
            <StyledSpacing pt={12}>
              {parentType === OFFER_TYPE_HOTEL &&
                <TextButton
                  kind="primary"
                  fit="flex"
                  data-testid="HotelAndFlightsBtn"
                  {...(!memberId && accountAccessShowLogin ? { onClick: showRegisterModalForFlight } : { to: bookingWithFlightPath })}
                >
                  Check Availability
                </TextButton>
              }
            </StyledSpacing>
          }
        </FlashTourSoldOutOrUnavailable>
      </StyledPriceContainer>
    }
  </PricesContainer>
}

export default memo(FlashTourPackagePricing)
